// src/components/WOEPayment/services/israelInvoiceService.js
import { getInvoicePrefix } from '../../../../utils/locationUtils';
import { toAPIDate } from '../../../../utils/dateUtils';
import { normalizeOrderType } from '../../../../utils/orderTypeUtils';

const israelInvoiceService = {
    
    // Create Israel local invoice
    createInvoice: async ({
        orderNo,
        customerInfo,
        orderItems,
        accessories,
        totals,
        payments,
        paymentSummary,
        agreementData,
        getCurrentUser
    }) => {
        console.log('🇮🇱 Creating Israel local invoice...');
        
        try {
            const invoiceData = israelInvoiceService.buildInvoiceData({
                orderNo,
                customerInfo,
                orderItems,
                accessories,
                totals,
                payments,
                paymentSummary,
                agreementData,
                getCurrentUser
            });
            
            // For now, we'll create a local invoice
            // In the future, you might want to save this to a separate Israel invoice table
            // or send to an Israeli accounting system
            
            const result = {
                success: true,
                localInvoice: true,
                invoiceNumber: invoiceData.invoiceNumber,
                invoiceData: invoiceData,
                message: `Israel invoice ${invoiceData.invoiceNumber} created successfully`,
                processingMode: 'LOCAL',
                location: 'ISRAEL'
            };
            
            console.log('✅ Israel invoice created successfully:', result.invoiceNumber);
            
            // Optional: Save to local storage for offline access
            israelInvoiceService.saveToLocalStorage(invoiceData);
            
            return result;
            
        } catch (error) {
            console.error('❌ Israel invoice creation failed:', error);
            return {
                success: false,
                localInvoice: true,
                error: error.message,
                message: 'Failed to create Israel invoice'
            };
        }
    },
    
    // Build complete invoice data structure
    buildInvoiceData: ({
        orderNo,
        customerInfo,
        orderItems,
        accessories,
        totals,
        payments,
        paymentSummary,
        agreementData,
        getCurrentUser
    }) => {
        const invoiceNumber = `${getInvoicePrefix()}${orderNo}`;
        const currentDate = new Date();
        
        return {
            // Invoice identification
            invoiceNumber: invoiceNumber,
            orderNo: orderNo,
            invoiceDate: toAPIDate(currentDate),
            dueDate: toAPIDate(currentDate), // Same day for Israel
            
            // Company information (Israel branch)
            company: {
                name: 'דיניס דיני בע״מ / Dinis Law Ltd.',
                address: 'Hamarpe 1 Har Chotzvim Mall, Jerusalem 4th floor', 
                phone: '+972.55.553.0300',
                email: 'israel@diniwigs.com', 
                vatNumber: '516107133', // real VAT number
                registrationNumber: '516107133' 
            },
            
            // Customer information
            customer: {
                name: customerInfo.customerName,
                email: customerInfo.email || '',
                phone: customerInfo.phone || '',
                address: customerInfo.address || '',
                city: customerInfo.city || '',
                state: customerInfo.state || '',
                zip: customerInfo.zip || '',
                country: 'Israel' // Assuming Israel customers
            },
            
            // Invoice line items
            items: [
                // Order items
                ...orderItems.map((item, index) => ({
                    lineNumber: index + 1,
                    description: item.description || `${item.type} - ${customerInfo.orderType}`,
                    quantity: parseInt(item.quantity) || 1,
                    unitPrice: parseFloat(item.price || 0),
                    total: (parseInt(item.quantity) || 1) * parseFloat(item.price || 0),
                    itemType: item.type || 'service',
                    category: 'order_item'
                })),
                // Accessories
                ...accessories.map((acc, index) => ({
                    lineNumber: orderItems.length + index + 1,
                    description: acc.description || acc.name || 'Accessory',
                    quantity: parseInt(acc.quantity) || 1,
                    unitPrice: parseFloat(acc.price || 0),
                    total: (parseInt(acc.quantity) || 1) * parseFloat(acc.price || 0),
                    itemType: 'accessory',
                    category: 'accessory'
                }))
            ],
            
            // Financial information (Israel - no tax, no card fees)
            financials: {
                subtotal: parseFloat(totals.subtotal),
                tax: 0, // Israel users don't pay tax in this system
                taxRate: 0,
                taxExemptReason: 'Israel location - local tax rules apply',
                total: parseFloat(totals.subtotal), // No tax for Israel
                currency: 'USD', // or 'ILS' if you want shekel invoices
                
                // Exchange rate (if needed for ILS)
                exchangeRate: 1.0, // You could fetch real USD/ILS rate here
                totalInLocalCurrency: parseFloat(totals.subtotal)
            },
            
            // Payment information
            payments: payments.map((payment, index) => ({
                paymentNumber: index + 1,
                method: payment.method,
                amount: parseFloat(payment.amount || 0),
                notes: payment.notes || '',
                cardFee: 0, // No card fees for Israel users
                paymentDate: toAPIDate(currentDate),
                reference: payment.notes || `${payment.method}-${orderNo}-${index + 1}`,
                currency: 'USD'
            })),
            
            // Payment summary
            paymentSummary: {
                totalPaid: parseFloat(paymentSummary.totalPaid),
                balanceDue: parseFloat(paymentSummary.balanceDue || 0),
                changeDue: parseFloat(paymentSummary.changeDue || 0),
                paymentStatus: paymentSummary.paymentStatus,
                totalCardFees: 0, // No card fees for Israel
                
                // Payment method breakdown
                paymentMethods: payments.reduce((acc, payment) => {
                    const method = payment.method;
                    const amount = parseFloat(payment.amount || 0);
                    acc[method] = (acc[method] || 0) + amount;
                    return acc;
                }, {}),
                
                // Business context
                isDepositPayment: normalizeOrderType(customerInfo.orderType) === 'New Wig',
                allowsPartialPayment: ['New Wig', 'Wig Repair'].includes(normalizeOrderType(customerInfo.orderType))
            },
            
            // Israel-specific metadata
            israelMetadata: {
                location: 'ISRAEL',
                processingMode: 'LOCAL',
                vatExempt: true, // Assuming VAT exempt for this system
                localTaxHandling: 'EXTERNAL', // Tax handled outside this system
                complianceNotes: 'Local Israel tax and regulatory compliance handled separately',
                
                // Regulatory information
                invoiceType: 'SERVICE_INVOICE',
                businessType: 'B2C', // Business to Consumer
                
                // Hebrew translations (if needed)
                hebrewTranslations: {
                    company: 'דיני ויגס ישראל',
                    invoice: 'חשבונית',
                    orderType: israelInvoiceService.translateOrderType(customerInfo.orderType),
                    total: 'סה"כ'
                }
            },
            
            // Order context
            orderMetadata: {
                orderType: customerInfo.orderType,
                orderDate: currentDate.toISOString(),
                createdBy: getCurrentUser(),
                
                // Agreement information
                agreementSigned: !!agreementData,
                agreementMethod: agreementData?.signatureMethod || null,
                agreementTimestamp: agreementData?.signedAt || null,
                
                // Special instructions
                notes: israelInvoiceService.buildInvoiceNotes({
                    customerInfo,
                    paymentSummary,
                    agreementData
                }),
                
                // Workflow information
                processingLocation: 'Israel',
                currency: 'USD',
                invoiceLanguage: 'EN', // Could be 'HE' for Hebrew
                
                // Customer type classification
                customerType: israelInvoiceService.classifyCustomer(customerInfo),
                priority: israelInvoiceService.determinePriority(customerInfo.orderType)
            },
            
            // System metadata
            systemMetadata: {
                createdAt: currentDate.toISOString(),
                createdBy: getCurrentUser(),
                version: '1.0.0',
                source: 'WOE_Israel_System',
                
                // Audit trail
                auditTrail: [
                    {
                        action: 'INVOICE_CREATED',
                        timestamp: currentDate.toISOString(),
                        user: getCurrentUser(),
                        details: 'Israel local invoice created'
                    }
                ],
                
                // Integration flags
                integrations: {
                    quickbooksSkipped: true,
                    localProcessing: true,
                    israelCompliant: true
                }
            }
        };
    },
    
    // Helper function to translate order types to Hebrew
    translateOrderType: (orderType) => {
        const translations = {
            'New Wig': 'פאה חדשה',
            'New Sale Wig': 'פאה למכירה',
            'Wig Repair': 'תיקון פאה',
            'Wash & Set': 'שטיפה ועיצוב',
            'Consultation': 'ייעוץ',
            'Accessory': 'אביזר',
            'Recut': 'חיתוך מחדש'
        };
        // "Aidel New Wig" / "Aidel Wig Repair" translate the same as their base type.
        return translations[normalizeOrderType(orderType)] || orderType;
    },
    
    // Build invoice notes
    buildInvoiceNotes: ({ customerInfo, paymentSummary, agreementData }) => {
        const notes = [];
        
        notes.push(`${customerInfo.orderType} Order - Israel Processing`);
        
        if (normalizeOrderType(customerInfo.orderType) === 'New Wig') {
            notes.push('Custom Manufacturing Order - Deposit Payment System');
        }
        
        if (parseFloat(paymentSummary.balanceDue || 0) > 0) {
            notes.push(`Balance Due: $${paymentSummary.balanceDue}`);
        } else {
            notes.push('Paid in Full');
        }
        
        if (agreementData) {
            const method = agreementData.signatureMethod === 'sms_remote' ? 'SMS' : 'In-Person';
            notes.push(`Customer Agreement Signed (${method})`);
        }
        
        notes.push('Israel Location - Local Processing Mode');
        notes.push('Tax handled according to local Israel regulations');
        
        return notes.join(' | ');
    },
    
    // Classify customer type
    classifyCustomer: (customerInfo) => {
        // "Aidel New Wig" / "Aidel Wig Repair" classify the same as their base type.
        const orderType = normalizeOrderType(customerInfo.orderType);
        if (orderType === 'New Wig') {
            return 'CUSTOM_MANUFACTURING';
        } else if (orderType === 'New Sale Wig') {
            return 'RETAIL_SALE';
        } else if (orderType === 'Wig Repair') {
            return 'SERVICE_REPAIR';
        } else if (orderType === 'Consultation') {
            return 'CONSULTATION_SERVICE';
        }
        return 'GENERAL';
    },
    
    // Determine order priority
    determinePriority: (orderType) => {
        const priorities = {
            'New Wig': 'HIGH',
            'New Sale Wig': 'MEDIUM',
            'Wig Repair': 'MEDIUM',
            'Consultation': 'LOW',
            'Accessory': 'LOW',
            'Wash & Set': 'LOW',
            'Recut': 'MEDIUM'
        };
        return priorities[normalizeOrderType(orderType)] || 'NORMAL';
    },
    
    // Save invoice to local storage for offline access
    saveToLocalStorage: (invoiceData) => {
        try {
            const key = `israel_invoice_${invoiceData.invoiceNumber}`;
            localStorage.setItem(key, JSON.stringify(invoiceData));
            
            // Also maintain a list of all Israel invoices
            const existingInvoices = JSON.parse(localStorage.getItem('israel_invoices_list') || '[]');
            if (!existingInvoices.includes(invoiceData.invoiceNumber)) {
                existingInvoices.push(invoiceData.invoiceNumber);
                localStorage.setItem('israel_invoices_list', JSON.stringify(existingInvoices));
            }
            
            console.log('💾 Israel invoice saved to localStorage:', invoiceData.invoiceNumber);
        } catch (error) {
            console.error('Failed to save Israel invoice to localStorage:', error);
        }
    },
    
    // Retrieve invoice from local storage
    getFromLocalStorage: (invoiceNumber) => {
        try {
            const key = `israel_invoice_${invoiceNumber}`;
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Failed to retrieve Israel invoice from localStorage:', error);
            return null;
        }
    },
    
    // Get all Israel invoices from local storage
    getAllFromLocalStorage: () => {
        try {
            const invoicesList = JSON.parse(localStorage.getItem('israel_invoices_list') || '[]');
            return invoicesList.map(invoiceNumber => {
                const invoice = israelInvoiceService.getFromLocalStorage(invoiceNumber);
                return invoice ? { invoiceNumber, ...invoice } : null;
            }).filter(Boolean);
        } catch (error) {
            console.error('Failed to retrieve Israel invoices list:', error);
            return [];
        }
    },
    
    // Export invoice to PDF/CSV (future implementation)
    exportInvoice: async (invoiceData, format = 'PDF') => {
        console.log(`📄 Exporting Israel invoice ${invoiceData.invoiceNumber} as ${format}`);
        
        // This would be implemented later with PDF generation or CSV export
        // For now, just log the intention
        
        return {
            success: true,
            message: `Invoice export as ${format} would be implemented here`,
            format: format,
            invoiceNumber: invoiceData.invoiceNumber
        };
    },
    
    // Send invoice via email (future implementation)
    sendInvoiceEmail: async (invoiceData, customerEmail) => {
        console.log(`📧 Sending Israel invoice ${invoiceData.invoiceNumber} to ${customerEmail}`);
        
        // This would integrate with your email service
        // For now, just log the intention
        
        return {
            success: true,
            message: `Invoice email sending would be implemented here`,
            recipient: customerEmail,
            invoiceNumber: invoiceData.invoiceNumber
        };
    }
};

export default israelInvoiceService;
